import React, { useState } from "react";
import { Flag, Share2 } from "lucide-react";
import ImageOrInitials from "../agency/ImageOrInitials";
import { CategoryPill, AttributePill, PriorityPill, LabelPill } from "./ScoutPills";
import PriorityLabelMenu from "./PriorityLabelMenu";
import { formatPositionAge, formatScoutDateAbsolute, formatScoutHorizon } from "../../utils/scoutFormat";
import { feeFramePillLabel } from "../../utils/scoutFeeFrame";
import { SCOUT_FEET } from "../../constants/scoutRequestChoices";

// Agent-facing browse card (design 1h / 2b / 7b). The club is deliberately OPEN
// here (Invariant 1: logo, name, country). There is NEVER a recommendation count or
// any other-agent signal on this surface. Prio + custom labels (design 5b/6a) are
// AGENCY-internal: they paint here for the agent, and the flag button opens the
// priority/label menu — the club never sees any of it.
const footLabel = (value) => {
  const meta = SCOUT_FEET.find((f) => f.value === value);
  return meta ? `${meta.label} foot` : "";
};

const AgentRequestCard = ({
  request,
  labels,
  onRecommend,
  onShare,
  onMarkChange,
  onCreateLabel,
  compact = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const mark = request.mark || { priority: null, labels: [] };
  const frame = feeFramePillLabel(request.fee_frame);

  return (
    <div className="bg-white rounded-xl p-5 border border-[#E5E7EB] shadow-[0_1px_2px_rgba(17,24,39,0.04)] flex flex-col">
      {/* Club (open) + category */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <ImageOrInitials src={request.club?.logo || null} alt={request.club?.name} size="large" />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold text-[#111827] truncate">{request.club?.name}</p>
            {request.club?.country && (
              <p className="text-[11.5px] text-[#6B7280] truncate flex items-center gap-1.5">
                {/* Flag only when the backend resolves one (name-based flag_url);
                    otherwise the country name alone (honest to the data). */}
                {request.club?.flag_url && (
                  <img
                    src={request.club.flag_url}
                    alt=""
                    className="w-[16px] h-[11px] rounded-[2px] object-cover flex-shrink-0"
                  />
                )}
                {request.club.country}
              </p>
            )}
          </div>
        </div>
        <CategoryPill category={request.category} />
      </div>

      {/* Agency-internal priority + labels (design 5b/6a) */}
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <PriorityPill priority={mark.priority} />
        {mark.labels?.map((l) => (
          <LabelPill key={l.id} label={l} />
        ))}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#6B7280] hover:text-[#111827] rounded-full px-2 py-0.5"
            aria-label="Set priority or labels"
            aria-expanded={menuOpen}
          >
            <Flag className="w-3.5 h-3.5" />
            {mark.priority || mark.labels?.length ? "Edit" : "Set priority"}
          </button>
          {menuOpen && (
            <PriorityLabelMenu
              requestId={request.id}
              mark={mark}
              labels={labels}
              onClose={() => setMenuOpen(false)}
              onMarkChange={onMarkChange}
              onCreateLabel={onCreateLabel}
            />
          )}
        </div>
      </div>

      {/* Position + age */}
      <p className="text-[15px] font-semibold text-[#111827] mt-3">{formatPositionAge(request)}</p>
      {/* Horizon + level */}
      <p className="text-[12.5px] text-[#6B7280] mt-1">
        Horizon: {formatScoutHorizon(request.horizon)}
        {request.level_expectation ? ` · ${request.level_expectation}` : ""}
      </p>

      {/* Profile facts + fee frame as calm grey pills */}
      {!compact && (
        <div className="flex items-center gap-2 flex-wrap mt-3">
          {request.preferred_foot && <AttributePill>{footLabel(request.preferred_foot)}</AttributePill>}
          {request.min_height_cm && <AttributePill>{`≥ ${request.min_height_cm} cm`}</AttributePill>}
          {frame && <AttributePill>{frame}</AttributePill>}
          {request.loan_possible && <AttributePill>Loan possible</AttributePill>}
        </div>
      )}

      {/* Context line */}
      {!compact && request.context_line && (
        <p className="text-[13px] italic text-[#374151] mt-3">&ldquo;{request.context_line}&rdquo;</p>
      )}

      {/* Footer — stacks on mobile with a full-width Recommend CTA (design 1t) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t border-[#F3F4F6] gap-2">
        <span className="text-[11.5px] text-[#9CA3AF]">Posted {formatScoutDateAbsolute(request.created_at)}</span>
        <div className="flex items-center gap-2 sm:flex-shrink-0">
          {/* Share the request into a chat as a card (design 5d, M4). */}
          <button
            type="button"
            onClick={() => onShare?.(request)}
            title="Share into chat"
            aria-label="Share into chat"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-[#6B7280] hover:text-[#111827] hover:bg-gray-50 flex-shrink-0"
            data-testid="share-request"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onRecommend(request)}
            className="flex-1 sm:flex-none min-h-[44px] px-3.5 py-2 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold hover:bg-[#1B6FE0]"
          >
            Recommend a player
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentRequestCard;
